import { toGoLang, toGoLangInvoke, asyncForEach } from "../common/windowFunctions";
import uuid from "uuid-random";
import { logStatus } from "../utils";
class Meta {
}
class NotificationImpl {
    constructor() {
        this.actions = [];
        this.isRead = false;
        this.isSnoozed = false;
        this.isMuted = false;
        this.isDeleted = false;
        this.actionsHistory = [];
        this.meta = new Meta();
        this.stateHistory = [];
        this.issuedAt = "";
        this.title = "";
        this.devModeOnly = false;
    }
}
class ActionImpl {
    constructor() {
        this.id = "";
        this.buttonText = "";
        this.type = "";
    }
}
class SubscriptionImpl {
    constructor(id, filter) {
        this.id = id ? id : null;
        this.filter = filter;
        this.channel = "";
    }
}
export class FilterImpl {
    constructor() {
        this.include = [];
        this.exclude = [];
    }
}
export var ActionTypesImpl;
(function (ActionTypesImpl) {
    ActionTypesImpl["DISMISS"] = "DISMISS";
    ActionTypesImpl["SNOOZE"] = "SNOOZE";
    ActionTypesImpl["SPAWN"] = "SPAWN";
    ActionTypesImpl["QUERY"] = "QUERY";
    ActionTypesImpl["PUBLISH"] = "PUBLISH";
    ActionTypesImpl["TRANSMIT"] = "TRANSMIT";
})(ActionTypesImpl || (ActionTypesImpl = {}));
const validateFinsembleFilterAgainstGlueNotificationOption = (notification, filter) => {
    if (!filter) {
        return true;
    }
    if (filter.exclude) {
        if (filter.exclude.find(({ type }) => type === notification.type)) {
            return false;
        }
        if (filter.exclude.find(({ source }) => source === notification.source)) {
            return false;
        }
    }
    if (filter.include && filter.include.length) {
        return filter.include.find((props) => !((props.type && props.type !== notification.type) || (props.source && props.source !== notification.source)));
    }
    return true;
};
const convertFinsembleActionToIOCDAction = (a, notification) => {
    return {
        action: `finsemble-notification-polyfill-${a.type}`,
        title: a.buttonText,
        interop: {
            method: "FSBL.Clients.NotificationClient.performAction",
            arguments: {
                id: notification.id,
                ...a,
                payload: {
                    notification,
                    actionPayload: a.payload
                }
            }
        }
    };
};
const finsembleNotificationToGlueNotificationOption = (n) => {
    const options = {
        title: n.title ?? "Notification"
    };
    options.id = n.id;
    if ("details" in n) {
        options.body = n.details;
    }
    if ("contentLogo" in n) {
        options.icon = n.contentLogo;
    }
    if ("timeout" in n) {
        options.toastExpiry = (n.timeout ?? 3000) / 1000;
    }
    if ("actions" in n) {
        options.actions = n.actions
            .flatMap((a) => {
            const iocdAction = convertFinsembleActionToIOCDAction(a, n);
            return iocdAction ? [iocdAction] : [];
        });
    }
    if ("type" in n) {
        options.type = n.type;
    }
    options.data = {
        ...n.meta
    };
    if ("cssClassName" in n) {
        options.data.cssClassName = n.cssClassName;
    }
    options.data.issuedAt = n.issuedAt ?? new Date();
    return options;
};
const glueNotificationOptionToFinsembleNotification = (option) => {
    const ms = option.timestamp ? option.timestamp * 1000 : new Date().getTime();
    let issuedAt = new Date(ms).toISOString();
    try {
        issuedAt = new Date(option.data?.issuedAt ?? ms).toISOString();
    }
    catch (e) {
    }
    return JSON.parse(JSON.stringify({
        actions: [],
        actionsHistory: [],
        stateHistory: [],
        id: option.id ?? uuid(),
        issuedAt,
        receivedAt: new Date(ms).toISOString(),
        meta: option.data,
        title: option.title,
        source: option.source,
        details: option.body,
        contentLogo: option.icon,
        timeout: option.toastExpiry,
        type: option.type,
        cssClassName: option.data?.cssClassName,
        isRead: option.state !== "Active",
        isDeleted: option.state === "Closed",
        isSnoozed: option.state === "Snoozed",
        isMuted: false
    }));
};
export class NotificationClientImpl {
    constructor(gluePromise, asyncConstructors, loggerClient) {
        this.gluePromise = gluePromise;
        this.asyncConstructors = asyncConstructors;
        this.loggerClient = loggerClient;
        this.Notification = NotificationImpl;
        this.ActionTypes = ActionTypesImpl;
        this.Subscription = SubscriptionImpl;
        this.Action = ActionImpl;
        this.Filter = FilterImpl;
        this.actionTypes = ActionTypesImpl;
        this.subscriptionMap = new Map();
        const pw = asyncConstructors.addConstructor();
        this.gluePromise.then((g) => {
            this.glue = g;
            asyncConstructors.finishConstructor(pw);
        });
    }
    logStatus(method, type, data) {
        logStatus(this.loggerClient, this.glue, {
            api: "NotificationClient",
            method, type, data
        });
    }
    async onClose(cb) {
        this.unsubscribeAll();
        cb?.();
    }
    async getPreferences() {
        this.logStatus("getPreferences", "start");
        await this.asyncConstructors.ready();
        const configs = await this.glue.notifications.getConfiguration();
        const result = {
            disableDotOnToolbarIcon: false,
            showDotOnToolbarIconForMutedNotifications: configs.showNotificationBadge,
            informationalNotificationToastPeriodSeconds: configs.toastExpiry / 1000,
            defaultSnoozePeriodSeconds: configs.snooze.duration,
            newNotificationHaloPeriodSeconds: 0,
            toastTimeoutOnActionableNotifications: true,
        };
        this.logStatus("getPreferences", "return", result);
        return result;
    }
    async getMuteFilters() {
        this.logStatus("getMuteFilters", "start");
        await this.asyncConstructors.ready();
        const filter = await this.glue.notifications.getFilter();
        const realFilter = filter.muted.map((source) => {
            return {
                source
            };
        });
        this.logStatus("getMuteFilters", "return", realFilter);
        return realFilter;
    }
    async getSources() {
        this.logStatus("getSources", "start");
        await this.asyncConstructors.ready();
        console.warn("NotificationClient.getSources() is not available in iocd");
        this.logStatus("getSources", "return", []);
        return [];
    }
    async unmute(filter) {
        this.logStatus("unmute", "start", filter);
        await this.asyncConstructors.ready();
        const actualFilter = (Array.isArray(filter) ? filter : [filter]).map((f) => f.source);
        const iocdFilter = await this.glue.notifications.getFilter();
        iocdFilter.muted = iocdFilter.muted.filter((source) => !actualFilter.includes(source));
        await this.glue.notifications.setFilter(iocdFilter);
        this.logStatus("unmute", "return");
    }
    async mute(filter) {
        this.logStatus("mute", "start", filter);
        await this.asyncConstructors.ready();
        const actualFilter = (Array.isArray(filter) ? filter : [filter]).map((f) => f.source);
        const iocdFilter = await this.glue.notifications.getFilter();
        iocdFilter.muted = iocdFilter.muted.concat(actualFilter);
        await this.glue.notifications.setFilter(iocdFilter);
        this.logStatus("mute", "return");
    }
    unsubscribeAll() {
        this.logStatus("unsubscribeAll", "start");
        this.subscriptionMap.forEach((value) => {
            value.unsubscribe();
        });
        this.subscriptionMap.clear();
        this.logStatus("unsubscribeAll", "return");
    }
    async unsubscribe(subscriptionId) {
        this.logStatus("unsubscribe", "start", subscriptionId);
        await this.asyncConstructors.ready();
        this.subscriptionMap.get(subscriptionId)?.unsubscribe?.();
        this.logStatus("unsubscribe", "return");
    }
    async subscribe(subscription, onNotification) {
        this.logStatus("subscribe", "start", subscription);
        await this.asyncConstructors.ready();
        const unsubscribe3 = this.glue.notifications.onClosed(async (glueNotification) => {
            this.logStatus("subscribe", "debug", `Received closed for ${glueNotification.id}`);
            glueNotification.isDeleted = true;
            onNotification(glueNotification);
        });
        const unsubscribe2 = this.glue.notifications.onStateChanged(async (glueNotification, state) => {
            this.logStatus("subscribe", "debug", `Received state ${state} for ${glueNotification.id}`);
            if (state === "Active")
                return;
            const notifications = await this.glue.notifications.list();
            const notification = notifications.find((n) => n.id === glueNotification.id);
            if (!notification) {
                this.logStatus("subscribe", "warn", "Can't find notification id for state change");
                return;
            }
            notification.isDeleted = true;
            if (!validateFinsembleFilterAgainstGlueNotificationOption(notification, subscription.filter)) {
                this.logStatus("subscribe", "debug", { message: "Filtered notification", notification });
                return;
            }
            onNotification(glueNotificationOptionToFinsembleNotification(notification));
        });
        const unsubscribe = this.glue.notifications.onRaised((glueNotification) => {
            if (!validateFinsembleFilterAgainstGlueNotificationOption(glueNotification, subscription.filter)) {
                this.logStatus("subscribe", "debug", { message: "Filtered notification", glueNotification });
                return;
            }
            onNotification(glueNotificationOptionToFinsembleNotification(glueNotification));
        });
        const id = subscription.id ?? uuid();
        this.subscriptionMap.set(id, {
            unsubscribe: () => {
                unsubscribe();
                unsubscribe2();
                unsubscribe3();
            }
        });
        this.logStatus("subscribe", "return", { id, channel: subscription.channel ?? "" });
        return {
            id,
            channel: subscription.channel ?? ""
        };
    }
    async notify(notifications) {
        this.logStatus("notify", "start", notifications);
        await this.asyncConstructors.ready();
        const notificationList = Array.isArray(notifications) ? notifications : [notifications];
        const existingNotifications = await this.glue.notifications.list();
        for (const n of notificationList) {
            const options = finsembleNotificationToGlueNotificationOption(n);
            const existingNotification = n.id && existingNotifications.some((n) => n.id === options.id);
            if (existingNotification) {
                await toGoLang(() => this.glue.notifications.clear(options.id));
            }
            const { err } = await toGoLang(() => this.glue.notifications.raise(options));
            if (err) {
                this.loggerClient.debug(`NotificationClient.Notify:raise ${err}`);
            }
            else {
                this.loggerClient.debug("[FSBL] [NotificationClient.notify] raised notification: ", n, " Post-process details", options);
            }
        }
        this.logStatus("notify", "return");
    }
    async deleteNotifications(notifications) {
        this.logStatus("deleteNotifications", "start", notifications);
        await this.asyncConstructors.ready();
        await asyncForEach(notifications, async (notification) => {
            if (notification.id)
                await this.glue.notifications.clear(notification.id);
        });
        this.logStatus("deleteNotifications", "return");
    }
    async markUnread(notifications) {
        this.logStatus("markUnread", "start", notifications);
        await this.asyncConstructors.ready();
        await asyncForEach(notifications, async (notification) => {
            if (notification.id)
                await this.glue.notifications.setState(notification.id, "Active");
        });
        this.logStatus("markUnread", "return");
    }
    async markRead(notifications) {
        this.logStatus("markRead", "start", notifications);
        await this.asyncConstructors.ready();
        await asyncForEach(notifications, async (notification) => {
            if (notification.id)
                await this.glue.notifications.setState(notification.id, "Acknowledged");
        });
        this.logStatus("markRead", "return");
    }
    async performAction(notifications, action) {
        this.logStatus("performAction", "start", { notifications, action });
        await this.asyncConstructors.ready();
        this.logStatus("performAction", "debug", { action, notifications });
        await asyncForEach(notifications, async (notification) => {
            const iocdAction = convertFinsembleActionToIOCDAction(action, notification);
            if (!iocdAction) {
                this.logStatus("performAction", "error", `Unable to parse action ${action}`);
                return;
            }
            await toGoLangInvoke(() => this.glue.interop.invoke(iocdAction.interop.method, iocdAction.interop.arguments));
        });
        this.logStatus("performAction", "return");
    }
    async getLastIssuedAt(source) {
        this.logStatus("getLastIssuedAt", "start", source);
        await this.asyncConstructors.ready();
        const result = await toGoLangInvoke(() => this.glue.interop.invoke("FSBL.Clients.NotificationClient.getLastIssuedAt", { source }));
        this.logStatus("getLastIssuedAt", "return", result);
        return result?.data?.lastIssuedAt ?? "";
    }
    async fetchHistory(options, filter) {
        this.logStatus("fetchHistory", "start", { options, filter });
        await this.asyncConstructors.ready();
        const normalizedOptions = typeof (options) === "string" ? { sentAfter: options } : options;
        const sentBefore = (normalizedOptions?.sentBefore ? new Date(normalizedOptions?.sentBefore) : new Date()).getTime();
        const sentAfter = (normalizedOptions?.sentAfter ? new Date(normalizedOptions?.sentAfter) : new Date(0)).getTime();
        const notifications = await this.glue.notifications.list();
        const filteredByDate = notifications.filter((notification) => {
            const date = (notification.timestamp ? new Date(notification.timestamp * 1000) : new Date()).getTime();
            if (sentBefore && date > sentBefore)
                return false;
            if (sentAfter && date < sentAfter)
                return false;
            return true;
        });
        const filterNotifications = (notifications, filter) => {
            const { exclude = [], include = [] } = filter;
            return notifications.filter((n) => {
                if (include.some((e) => e.type === n.type || e.source === n.source))
                    return true;
                if (exclude.some((e) => e.type === n.type || e.source === n.source))
                    return false;
            });
        };
        const filtered = filter ? filterNotifications(filteredByDate, filter) : filteredByDate;
        const response = filtered.map((notification) => glueNotificationOptionToFinsembleNotification(notification));
        this.logStatus("fetchHistory", "return", response);
        return response;
    }
}
